import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { ShortsRow, VideoGrid } from "@/components/video/VideoCard";
import { VideoEditDialog } from "@/components/video/VideoEditDialog";

// Lists a creator's videos; owners get edit/delete controls.
export const ProfileVideos = ({ username, manage = false }) => {
  const { request, refresh } = useProfile();
  const [videos, setVideos] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  useEffect(() => { request("get", `/profiles/${username}/videos`).then(setVideos).catch(() => setVideos([])); }, [username, request]);

  const remove = async () => {
    try { await request("delete", `/videos/${deleting.id}`); setVideos((vs) => vs.filter((v) => v.id !== deleting.id)); refresh(); toast.success("Video removed"); }
    catch (err) { toast.error(errorMessage(err)); } finally { setDeleting(null); }
  };

  if (!videos) return null;
  const actions = manage ? (v) => (
    <div className="flex gap-1 shrink-0">
      <button data-testid={`video-edit-${v.id}`} onClick={() => setEditing(v)} aria-label="Edit video" className="p-1.5 text-slate-400 hover:text-white"><Pencil className="h-4 w-4" /></button>
      <button data-testid={`video-delete-${v.id}`} onClick={() => setDeleting(v)} aria-label="Delete video" className="p-1.5 text-slate-400 hover:text-nw-red"><Trash2 className="h-4 w-4" /></button>
    </div>
  ) : undefined;
  const shorts = videos.filter((v) => v.is_short), longs = videos.filter((v) => !v.is_short);

  return (
    <section data-testid="profile-videos-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-white">{manage ? "My videos" : "Videos"}</h2>
        {manage && <Link to="/upload?type=video" data-testid="my-videos-upload-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-nw-red hover:underline"><UploadCloud className="h-4 w-4" /> Upload</Link>}
      </div>
      {shorts.length > 0 && <ShortsRow videos={shorts} renderActions={actions} />}
      {(longs.length > 0 || shorts.length === 0) && <VideoGrid videos={longs} empty={manage ? "You haven't uploaded any videos yet." : "No videos released yet."} renderActions={actions} />}
      <VideoEditDialog video={editing} onClose={() => setEditing(null)} onSaved={(u) => { setVideos((vs) => vs.map((v) => (v.id === u.id ? u : v))); setEditing(null); }} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-nw-bg2 border-white/10 text-slate-100 max-w-[92vw] sm:max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">The video will be removed from NEW WAVE.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="video-delete-cancel" className="bg-transparent border-white/15 text-slate-200 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="video-delete-confirm" onClick={remove} className="bg-nw-red hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
